import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Download, Maximize2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface BeforeAfterComparisonProps {
  beforeImage: string;
  afterImage: string;
  beforeLabel?: string;
  afterLabel?: string;
  onDownload?: () => void;
}

export const BeforeAfterComparison: React.FC<BeforeAfterComparisonProps> = ({
  beforeImage,
  afterImage,
  beforeLabel = 'Before',
  afterLabel = 'After',
  onDownload,
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [view, setView] = useState<'slider' | 'side-by-side'>('slider');
  const [isFullscreen, setIsFullscreen] = useState(false);

  const ComparisonContent = () => (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          size="sm"
          variant={view === 'slider' ? 'default' : 'outline'}
          onClick={() => setView('slider')}
        >
          Slider View
        </Button>
        <Button
          size="sm"
          variant={view === 'side-by-side' ? 'default' : 'outline'}
          onClick={() => setView('side-by-side')}
        >
          Side by Side
        </Button>
        {onDownload && (
          <Button size="sm" variant="outline" onClick={onDownload} className="ml-auto">
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        )}
        {!isFullscreen && (
          <Button size="sm" variant="outline" onClick={() => setIsFullscreen(true)}>
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {view === 'slider' ? (
        <div className="relative w-full aspect-video bg-muted rounded-lg overflow-hidden">
          <img
            src={beforeImage}
            alt={beforeLabel}
            className="absolute inset-0 w-full h-full object-contain"
          />
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
          >
            <img
              src={afterImage}
              alt={afterLabel}
              className="absolute inset-0 w-full h-full object-contain"
            />
          </div>
          <div
            className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
            style={{ left: `${sliderPosition}%` }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
              <div className="w-1 h-4 bg-gray-400" />
            </div>
          </div>
          <div className="absolute top-4 left-4 bg-black/60 text-white px-3 py-1 rounded text-sm">
            {beforeLabel}
          </div>
          <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded text-sm">
            {afterLabel}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">{beforeLabel}</p>
            <img
              src={beforeImage}
              alt={beforeLabel}
              className="w-full aspect-video object-contain bg-muted rounded-lg"
            />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">{afterLabel}</p>
            <img
              src={afterImage}
              alt={afterLabel}
              className="w-full aspect-video object-contain bg-muted rounded-lg"
            />
          </div>
        </div>
      )}

      {view === 'slider' && (
        <div className="px-4">
          <Slider
            value={[sliderPosition]}
            onValueChange={([value]) => setSliderPosition(value)}
            min={0}
            max={100}
            step={1}
          />
        </div>
      )}
    </div>
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Comparison</CardTitle>
          <CardDescription>
            Compare your original and processed images
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ComparisonContent />
        </CardContent>
      </Card>

      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Image Comparison</DialogTitle>
          </DialogHeader>
          <ComparisonContent />
        </DialogContent>
      </Dialog>
    </>
  );
};
